/**
 * runtimeConnectionService — Sprint 17 (Connection Runtime Engine).
 *
 * Bridges the active workspace connection to an isolated runtime instance.
 * Loads the connection (with secrets) via the shared runtime config provider,
 * opens (or reuses) its runtime Firebase app, and points the live `runtimeDb`
 * binding at that customer project so every data-access service targets it.
 */
import { resolveConnectionRuntimeConfig } from "./connectionRuntimeConfigService";
import { getRuntimeInstance, disposeAllExcept, disposeRuntimeInstance } from "./connectionRuntimeService";
import { setRuntimeContext, resetRuntimeDb, getRuntimeVersion } from "./runtimeDb";
import {
  logRuntimeCreated,
  logRuntimeDisposed,
  logRuntimeRefreshCompleted,
} from "./runtimeLogger";

/**
 * Resolve the runtime instance for a connection id and activate its Firestore
 * as the global runtime binding.
 *
 * @returns {Promise<{ connection, instance }>}
 */
export async function activateRuntimeConnection(connectionId) {
  if (!connectionId) {
    resetRuntimeDb();
    logRuntimeDisposed({ connectionId: null, projectId: null, runtimeVersion: getRuntimeVersion() });
    return { connection: null, instance: null };
  }

  const connection = await resolveConnectionRuntimeConfig(connectionId);
  if (!connection) {
    resetRuntimeDb();
    throw new Error("Active connection not found. Choose a connection in the workspace bar.");
  }

  const instance = getRuntimeInstance(connectionId, connection);
  const projectId = connection.firebaseProjectId || connection.projectId || instance.app?.options?.projectId;
  setRuntimeContext({
    firestore: instance.firestore,
    app: instance.app,
    auth: instance.auth,
    connectionId,
    projectId,
  });

  const runtimeVersion = getRuntimeVersion();
  logRuntimeCreated({
    connectionId,
    projectId,
    appName: instance.app?.name,
    runtimeVersion,
  });
  logRuntimeRefreshCompleted({ connectionId, projectId, runtimeVersion });

  // Free memory from any previously active runtime apps.
  await disposeAllExcept(connectionId);

  return { connection, instance };
}

/**
 * Deactivate the current runtime connection, disposing its app and resetting
 * the runtime binding back to the Studio/default Firestore.
 */
export async function deactivateRuntimeConnection(connectionId) {
  const disposedProjectId = connectionId ? null : null;
  if (connectionId) await disposeRuntimeInstance(connectionId);
  resetRuntimeDb();
  logRuntimeDisposed({
    connectionId: connectionId || null,
    projectId: disposedProjectId,
    runtimeVersion: getRuntimeVersion(),
  });
}

/**
 * Read-only helper for non-provider callers that need the runtime Firestore for
 * a specific connection (e.g. AI / agents / workflows) without touching the
 * global binding. Reuses the shared runtime cache.
 */
export async function openRuntimeFirestore(connectionId) {
  if (!connectionId) throw new Error("No connection selected. Choose a connection in the workspace bar.");
  const connection = await resolveConnectionRuntimeConfig(connectionId);
  if (!connection) throw new Error("Connection not found.");
  const instance = getRuntimeInstance(connectionId, connection);
  return {
    firestore: instance.firestore,
    auth: instance.auth,
    storage: instance.storage,
    functions: instance.functions,
    connection,
    // Cached & centrally managed — cleanup is a no-op to avoid tearing down a
    // shared runtime app that other modules may still be using.
    async cleanup() {},
  };
}
